import type {
  ExternalWorkIntegrationStatusValues,
  JiraCustomFieldFilter,
  WorkIntegration_Keys,
} from '@/common/api/types/workIntegrations';

export const workIntegrationsQueryKeys = {
  workIntegrations: ['workIntegrations'],
  workIntegrationsAll: (apiOptions?: {
    limit?: number;
    filter?: {
      statuses?: ExternalWorkIntegrationStatusValues[];
      ids?: number[];
      loopIds?: number[];
    };
  }) => ['workIntegrations', apiOptions],
  instances: () => ['workIntegrations', 'instances'],
  authDescriptor: (provider: WorkIntegration_Keys) => [
    'workIntegrations',
    'authDescriptor',
    provider,
  ],
  jiraInstanceIssueTypes: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraInstanceIssueTypes',
    { instanceId },
  ],
  jiraItemLevelMappings: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraItemLevelMappings',
    { instanceId },
  ],
  jiraItemTypeMappings: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraItemTypeMappings',
    { instanceId },
  ],
  jiraFields: ({
    credentialsId,
    instanceId,
    projectId,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectId?: string;
  }) => ['workIntegrations', 'jiraFields', { credentialsId, instanceId, projectId }],
  jiraCustomFields: ({
    credentialsId,
    instanceId,
    projectId,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectId?: string;
  }) => ['workIntegrations', 'jiraCustomFields', { credentialsId, instanceId, projectId }],
  jiraFieldOptions: ({
    credentialsId,
    instanceId,
    fieldId,
    fieldValue,
  }: {
    credentialsId?: number;
    instanceId?: string;
    fieldId?: string;
    fieldValue?: string;
  }) => [
    'workIntegrations',
    'jiraFieldOptions',
    { credentialsId, instanceId, fieldId, fieldValue },
  ],
  jiraUserSuggestions: ({
    credentialsId,
    instanceId,
    searchTerm,
  }: {
    credentialsId?: number;
    instanceId?: string;
    searchTerm?: string;
  }) => ['workIntegrations', 'jiraUserSuggestions', { credentialsId, instanceId, searchTerm }],
  jiraUserBulk: ({
    credentialsId,
    instanceId,
    accountIds,
  }: {
    credentialsId?: number;
    instanceId?: string;
    accountIds?: string[];
  }) => [
    'workIntegrations',
    'jiraUserBulk',
    { credentialsId, instanceId, accountIds: accountIds ? [...accountIds].sort() : undefined },
  ],
  jiraProjectVersions: ({
    credentialsId,
    instanceId,
    projectKey,
    includeArchived,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectKey?: string;
    includeArchived?: boolean;
  }) => [
    'workIntegrations',
    'jiraProjectVersions',
    { credentialsId, instanceId, projectKey, includeArchived },
  ],
  jiraProjectComponents: ({
    credentialsId,
    instanceId,
    projectKey,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectKey?: string;
  }) => ['workIntegrations', 'jiraProjectComponents', { credentialsId, instanceId, projectKey }],
  jiraProjects: ({ cloudId }: { cloudId?: string }) => [
    'workIntegrations',
    'jiraProjects',
    { cloudId },
  ],
  adoTeamProjects: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'adoTeamProjects',
    { instanceId },
  ],
  adoWorkItemTypes: ({
    instanceId,
    projectName,
  }: {
    instanceId?: string;
    projectName?: string;
  }) => ['workIntegrations', 'adoWorkItemTypes', { instanceId, projectName }],
  adoWiqlValidation: ({
    instanceId,
    projectName,
    wiql,
  }: {
    instanceId?: string;
    projectName?: string;
    wiql?: string;
  }) => ['workIntegrations', 'adoWiqlValidation', { instanceId, projectName, wiql }],
  jiraIssueTypesAndStatuses: ({
    credentialsId,
    cloudId,
    projectIdOrKey,
  }: {
    credentialsId?: number;
    cloudId?: string;
    projectIdOrKey?: string;
  }) => [
    'workIntegrations',
    'jiraIssueTypesAndStatuses',
    { credentialsId, cloudId, projectIdOrKey },
  ],
  jiraIssueTypesWithHierarchy: ({
    credentialsId,
    cloudId,
    projectIdOrKey,
  }: {
    credentialsId?: number;
    cloudId?: string;
    projectIdOrKey?: string;
  }) => [
    'workIntegrations',
    'jiraIssueTypesWithHierarchy',
    { credentialsId, cloudId, projectIdOrKey },
  ],
  jiraWorkItemCount: ({
    cloudId,
    projectId,
    typeIds = [],
    statusIds = [],
    includeWorkAfter,
    jql,
    customFieldFilters = [],
  }: {
    cloudId?: string;
    projectId?: string;
    typeIds?: string[];
    statusIds?: string[];
    includeWorkAfter?: string;
    jql?: string;
    customFieldFilters?: JiraCustomFieldFilter[];
  }) => [
    'workIntegrations',
    'jiraWorkItemCount',
    {
      cloudId,
      projectId,
      typeIds,
      statusIds,
      includeWorkAfter,
      jql,
      customFieldFilters,
    },
  ],
  jiraJqlValidation: ({
    credentialsId,
    cloudId,
    jql,
  }: {
    credentialsId?: number;
    cloudId?: string;
    jql?: string;
  }) => ['workIntegrations', 'jiraJqlValidation', { credentialsId, cloudId, jql }],
  workItemPreview: ({ projectId }: { projectId?: string }) => [
    'workIntegrations',
    'workItemPreview',
    { projectId },
  ],
  workIntegrationErrors: ({ workIntegrationId }: { workIntegrationId?: number }) => [
    'workIntegrations',
    'errors',
    { workIntegrationId },
  ],
  pendingItemsCount: ({ integrationId }: { integrationId?: number }) => [
    'workIntegrations',
    'pendingItemsCount',
    { integrationId },
  ],
  syncFailedItemsAll: () => ['workIntegrations', 'syncFailedItems'],
  syncFailedItemsForIntegration: ({
    integrationId,
    page,
    pageSize,
  }: {
    integrationId?: number;
    page?: number;
    pageSize?: number;
  }) => ['workIntegrations', 'syncFailedItems', 'integration', { integrationId, page, pageSize }],
  syncFailedItemsForItem: ({
    itemIdOrSourceItemKey,
    page,
    pageSize,
  }: {
    itemIdOrSourceItemKey?: string;
    page?: number;
    pageSize?: number;
  }) => ['workIntegrations', 'syncFailedItems', 'item', { itemIdOrSourceItemKey, page, pageSize }],
  workIntegrationStatus: () => ['workIntegrations', 'status'],
  workIntegrationItemsPrefix: () => ['workIntegrations', 'items'] as const,
  workIntegrationItems: ({ itemIds }: { itemIds?: Set<number> }) => [
    'workIntegrations',
    'items',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  itemSyncStatusesPrefix: () => ['workIntegrations', 'itemSyncStatuses'] as const,
  itemSyncStatuses: ({ itemIds }: { itemIds?: Set<number> }) => [
    'workIntegrations',
    'itemSyncStatuses',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  schedules: ({ itemIds }: { itemIds?: Set<number> }) => [
    'schedules',
    'items',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  scheduleByItemId: (itemId: number) => ['schedules', 'single', itemId] as const,
  integrationSchedule: ({ integrationId }: { integrationId: number | undefined }) =>
    ['workIntegrations', 'schedule', { integrationId }] as const,
  estimations: {
    byIds: (estimationIds: Set<number>) => [
      'estimations',
      'byIds',
      { estimationIds: estimationIds.size > 0 ? Array.from(estimationIds).sort() : undefined },
    ],
  },
  integrationInstances: {
    all: () => ['workIntegrations', 'registered-instances'],
    list: (sourceSystemType?: WorkIntegration_Keys) => [
      ...workIntegrationsQueryKeys.integrationInstances.all(),
      { sourceSystemType },
    ],
  },
  instanceFieldMappings: ({
    numericInstanceId,
    projectId,
  }: {
    numericInstanceId?: number;
    projectId?: string;
  }) => ['workIntegrations', 'instanceFieldMappings', { numericInstanceId, projectId }],
  instanceFieldMappingUsage: ({ numericInstanceId }: { numericInstanceId?: number }) => [
    'workIntegrations',
    'instanceFieldMappingUsage',
    { numericInstanceId },
  ],
  fieldValueMappings: ({ instanceFieldMappingId }: { instanceFieldMappingId?: number }) => [
    'workIntegrations',
    'fieldValueMappings',
    { instanceFieldMappingId },
  ],
  loopFieldValues: ({ instanceFieldMappingId }: { instanceFieldMappingId?: number }) => [
    'workIntegrations',
    'loopFieldValues',
    { instanceFieldMappingId },
  ],
  fieldValues: ({ instanceFieldMappingId }: { instanceFieldMappingId?: number }) => [
    'workIntegrations',
    'fieldValues',
    { instanceFieldMappingId },
  ],
  fieldMappingSummary: ({ instanceFieldMappingId }: { instanceFieldMappingId?: number }) => [
    'workIntegrations',
    'fieldMappingSummary',
    { instanceFieldMappingId },
  ],
  integrationCustomFieldMappings: ({ integrationId }: { integrationId?: number }) => [
    'workIntegrations',
    'integrationCustomFieldMappings',
    { integrationId },
  ],
  pushCapability: (sourceSystemType: string | undefined) => [
    'workIntegrations',
    'pushCapability',
    sourceSystemType,
  ],
  capabilityDescriptor: (integrationId: number | undefined) => [
    'workIntegrations',
    'capabilityDescriptor',
    integrationId,
  ],
  oauthCredentialStatuses: {
    all: () => ['workIntegrations', 'oauthCredentialStatuses'],
    byIds: ({ credentialIds }: { credentialIds: number[] }) => [
      ...workIntegrationsQueryKeys.oauthCredentialStatuses.all(),
      { credentialIds },
    ],
  },
  ado: {
    accounts: (credentialsId: number | undefined) => [
      'workIntegrations',
      'ado',
      'accounts',
      { credentialsId },
    ],
    teamProjects: ({ instanceId }: { instanceId?: string }) => [
      'workIntegrations',
      'ado',
      'teamProjects',
      { instanceId },
    ],
    workItemTypes: ({ instanceId, projectName }: { instanceId?: string; projectName?: string }) => [
      'workIntegrations',
      'ado',
      'workItemTypes',
      { instanceId, projectName },
    ],
    wiqlValidation: ({
      instanceId,
      projectName,
      wiql,
    }: {
      instanceId?: string;
      projectName?: string;
      wiql?: string;
    }) => ['workIntegrations', 'ado', 'wiqlValidation', { instanceId, projectName, wiql }],
  },
};
